import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

import { definePersistenceEntry } from '../persistence/registry'
import type { ModulePersistenceEntry, SliceDeclaration } from '../persistence/types'

/**
 * Переключатель «Подсказки» в шапке (UI-ACTIONS): рисовать ли ⓘ у кнопок.
 * По умолчанию включено — рекомендация плана, владелец не возражал; на
 * телефоне (до 720 px) — выключено: ⓘ у каждой кнопки съедали ширину, шапка
 * уходила в две строки, а текст плиток наезжал на значки (мобильный аудит
 * 15.10, п. 9). Включить можно той же кнопкой «Подсказки». Выбор —
 * настройка устройства (как тема): переживает перезагрузку и смену человека
 * за кассой, данных сервера не содержит.
 */
export interface ActionHintsState {
  enabled: boolean
}

/** Ширина телефона в шапке оболочки (`shell.css`, `@media (max-width: 720px)`). */
const PHONE_QUERY = '(max-width: 720px)'

/** Без сохранённого выбора: на телефоне выключено, иначе включено. Сохранённый выбор приходит потом `actionHintsRestored`. */
export function defaultActionHints(): ActionHintsState {
  const phone = typeof globalThis.matchMedia === 'function' && globalThis.matchMedia(PHONE_QUERY).matches
  return { enabled: !phone }
}

/** Срок жизни (D-070). Composition добавляет префикс оболочки: `shell.actionHints`. */
export const ACTION_HINTS_SLICES: readonly SliceDeclaration[] = [
  { name: 'actionHints', lifetime: 'device', reason: 'показывать ли подсказки ⓘ — настройка устройства, как тема' },
]

export function decodeActionHints(raw: unknown): boolean | undefined {
  return typeof raw === 'boolean' ? raw : undefined
}

const actionHintsSlice = createSlice({
  name: 'actionHints',
  initialState: defaultActionHints,
  reducers: {
    actionHintsToggled(state) {
      state.enabled = !state.enabled
    },
    actionHintsRestored(state, action: PayloadAction<boolean>) {
      state.enabled = action.payload
    },
  },
})

export const { actionHintsToggled, actionHintsRestored } = actionHintsSlice.actions
export const actionHintsReducer = actionHintsSlice.reducer

export const selectActionHintsEnabled = (state: ActionHintsState): boolean => state.enabled

/** Запись реестра хранения: устройство, не человек (`scope: 'device'`). */
export const ACTION_HINTS_PERSISTENCE: readonly ModulePersistenceEntry<ActionHintsState>[] = [
  definePersistenceEntry({
    key: 'shell.action-hints',
    slice: 'actionHints',
    lifetime: 'device',
    scope: 'device',
    version: 1,
    pick: (state: ActionHintsState) => state.enabled,
    decode: decodeActionHints,
    restore: (value) => actionHintsRestored(value),
  }),
]
